import {
  observable, action, set, computed,
} from 'mobx';
import PAYMENT_PROVIDERS, { PAYMENT_MODES } from '../../../constants/PaymentProviders';
import GiftCard from '../GiftCard/GiftCard';
import { diContainer, serviceNames } from '../../../diContainer/diContainer';
import Config, { IPayPalSettings } from '../Config';
import { IAcceptedCreditCards, IAcceptedCashOptions } from '../../../api/IApiResponses';
import Order from '../Order/Order';
import { getPaymentDescriptorId } from '../../../payment-strategies/PaymentDescriptor';
import { IScriptAttr } from '../../../utils/DomUtils';
import { CoreConfiguration } from '@adyen/adyen-web';

export interface IPayment {
  selectedPayment?: string;
  selectedPaymentMode?: string;
  giftCards: GiftCard[];
  order: Order;
  paymentProviders: PaymentProvider[];
}

export interface IAfterpayLimis {
  minValue: string;
  maxValue: string;
}

export interface PaymentProvider {
  paymentType: string;
  providerName: string;
  paymentName?: string;
  paymentMode?: string;
  paymentOption?: string;
  providerLocale?: string;
  isGiftCardSupported?: boolean;
  clientKey?: string;
  acceptedCreditCards?: IAcceptedCreditCards[];
  acceptedCashOptions?: IAcceptedCashOptions[];
  logoImgPath: string;
  introImgPath?: string;
  sdks: IScriptAttr[];
  isDefault: boolean;
  isAvailable: boolean;
  maxQuantity?: number;
  payLaterEligible?: boolean;
  payPalSettings?: IPayPalSettings;
  venmoProfileId: string;
  adyenWebEnvironment: CoreConfiguration['environment'];
  enableCCSaveSetDefault?: boolean;
  enableInstallments?: boolean;
  disableCCSaveNextPayment: boolean;
  isTestServer?: boolean;
  hasHolderName?: boolean;
  hideCVC?: boolean;
  isHolderNameRequired?: boolean;
  showAsDisabled: boolean;
  disabledLogoImgPath?: string;
  afterpayLimits?: IAfterpayLimis;
  installmentsNumber?: number;
  hideTermsAndConditions?: boolean;
}

class Payment {
  // adyen payment session
  @observable paymentSession = '';

  // afterpay payment session
  @observable paymentToken = '';

  // afterpay country code
  @observable countryCode = '';

  // GSI paypal payment data
  @observable paymentData = '';

  @observable selectedPayment: PAYMENT_PROVIDERS;

  @observable selectedPaymentMode: PAYMENT_MODES;

  @observable giftCards: GiftCard[] = [];

  @observable config: Config;

  @observable order: Order;

  @observable paymentProviders: PaymentProvider[] = [];

  constructor() {
    this.config = diContainer.get(serviceNames.config);
  }

  @computed
  get paymentProvidersMap(): { [key: string]: PaymentProvider } {
    return (this.paymentProviders || []).reduce((acc, item) => ({
      ...acc,
      [getPaymentDescriptorId(item.providerName, item.paymentMode)]: item,
    }), {});
  }

  @computed
  get maxGiftCards(): number {
    const giftCardPayment = this.paymentProviders
      .find(provider => provider.providerName?.toLowerCase() === 'giftcard');

    return giftCardPayment?.maxQuantity || 0;
  }

  @computed
  get defaultPayment(): PaymentProvider {
    const defaultPayment = this.paymentProviders
      .find(provider => provider.isDefault);

    return defaultPayment || this.paymentProviders?.filter(item => item.isAvailable)[0];
  }

  static fromJSON(data: IPayment): Payment {
    const payment = new Payment();
    set(payment, {
      giftCards: data.giftCards.map(gc => GiftCard.fromJSON(gc)),
    });
    set(payment, {
      order: data.order,
      paymentProviders: data.paymentProviders,
      selectedPayment: data.selectedPayment,
      selectedPaymentMode: data.selectedPaymentMode,
    });

    return payment;
  }

  update(data: Payment): void {
    set(this, {
      giftCards: data.giftCards.map(gc => GiftCard.fromJSON(gc)),
      order: data.order,
      paymentProviders: data.paymentProviders,
      selectedPayment: data.selectedPayment,
      selectedPaymentMode: data.selectedPaymentMode,
    });
  }

  @action
  setSelectedPayment(payment: PAYMENT_PROVIDERS): void {
    this.selectedPayment = payment;
  }

  @action
  setSelectedPaymentMode(paymentMode: PAYMENT_MODES): void {
    this.selectedPaymentMode = paymentMode;
  }

  @action
  updatePaymentSession(paymentSession: string): void {
    this.paymentSession = paymentSession;
  }

  @action
  updatePaymentToken(paymentToken: string): void {
    this.paymentToken = paymentToken;
  }

  @action
  updateCountryCode(countryCode: string): void {
    this.countryCode = countryCode;
  }

  @action
  updatePaymentData(paymentData: string): void {
    this.paymentData = paymentData;
  }

  getGiftCardByNumber = (number: number | string): GiftCard => this.giftCards.find(
    (gc: GiftCard) => gc.number === number,
  );

  @action
  addGiftCard = (giftCard: GiftCard): void => {
    this.giftCards.push(giftCard);
  };

  @action
  removeGiftCard = (giftCard: GiftCard): void => {
    const giftCardIndex = this.giftCards.findIndex((gc: GiftCard) => gc.number === giftCard.number);
    this.giftCards.splice(giftCardIndex, 1);
  };

  @computed
  get areGiftCardsApplied(): boolean {
    return this.giftCards.length > 0;
  }

  @computed
  get checkoutWithoutPayment(): boolean {
    return this.config && this.config.canCheckoutWithoutPayment
     && this.order && this.order.totalCost === 0;
  }

  isPaymentMethodAvailable = (paymentMethod: PAYMENT_PROVIDERS): boolean => this.config
    && this.paymentProviders
      ?.some(p => p.providerName === paymentMethod && p.isAvailable);

  isPaymentPresent = (paymentMethod: PAYMENT_PROVIDERS): boolean => this.config
    && this.paymentProviders
      ?.some(p => p.providerName === paymentMethod);

  isPaymentMethodAndModeAvailable = (paymentMethod: PAYMENT_PROVIDERS, paymentMode: PAYMENT_MODES):
  boolean => this.paymentProviders
    ?.some(p => p.providerName === paymentMethod && p.paymentMode === paymentMode);

  isGiftCardPaymentSupportedByMethod = (paymentMethod: string): boolean => this.config
    && this.paymentProviders
      ?.some(item => item.providerName === paymentMethod && item.isGiftCardSupported);

  isGiftCardPaymentSupportedByMode = (paymentMode: string):
  boolean => this.paymentProviders
    ?.some(item => item.paymentMode === paymentMode && item.isGiftCardSupported);

  isGiftCardPaymentSupportedByMethodAndMode = (
    paymentMethod: string,
    paymentMode: string,
  ): boolean => {
    // paymentMode doesn't exist for some payment providers thus returning event object due to bind
    // to differentiate with event object and actual paymentMode type string check is used
    if (typeof paymentMode === 'string') {
      return this.isGiftCardPaymentSupportedByMode(paymentMode);
    }
    return this.isGiftCardPaymentSupportedByMethod(paymentMethod);
  };

  @computed
  get hasMultiplePaymentMethods(): boolean {
    return this.paymentProviders?.length > 1;
  }

  getPaymentMode = (paymentMethod: PAYMENT_PROVIDERS): string => this.config
    && this.paymentProviders.find(p => p.providerName === paymentMethod).paymentMode;

  getPaymentProviderConfig = (paymentMethod: PAYMENT_PROVIDERS, paymentMode?: PAYMENT_MODES):
  PaymentProvider => this.paymentProviders
    ?.find(p => p.providerName === paymentMethod && (
      p.paymentMode === paymentMode || !paymentMode
    ));

  getAcceptedCreditCards = (paymentMethod: PAYMENT_PROVIDERS, paymentMode?: PAYMENT_MODES):
  IAcceptedCreditCards[] => {
    const paymentProvider = this.getPaymentProviderConfig(paymentMethod, paymentMode);

    return paymentProvider?.acceptedCreditCards ?? [];
  };

  @computed
  get isPayByLink(): boolean {
    const paymentMethod = this.paymentProviders
      ?.find(p => p.providerName === PAYMENT_PROVIDERS.ADYEN_WEB && p.paymentMode === PAYMENT_MODES.PAY_BY_LINK);

    return !!paymentMethod;
  }

  @computed
  get payPalSettings(): IPayPalSettings {
    const paymentProvider = this.paymentProviders
      .find(p => p.providerName === PAYMENT_PROVIDERS.BRAINTREE);

    return paymentProvider?.payPalSettings;
  }

  @computed
  get payLaterEligible(): boolean {
    const paymentProvider = this.paymentProviders
      .find(p => p.providerName === PAYMENT_PROVIDERS.BRAINTREE);

    return paymentProvider?.payLaterEligible;
  }

  @computed
  get venmoProfileId(): string {
    const paymentProvider = this.paymentProviders
      .find(p => p.providerName === PAYMENT_PROVIDERS.BRAINTREE && p.paymentMode === PAYMENT_MODES.VENMO);

    return paymentProvider?.venmoProfileId;
  }

  @computed
  get afterPayLimits(): IAfterpayLimis {
    const paymentProvider = this.paymentProviders
      .find(p => p.providerName === PAYMENT_PROVIDERS.AFTERPAY);

    return paymentProvider?.afterpayLimits;
  }

  @computed
  get klarnaInstallmentsNumber(): number {
    const paymentProvider = this.paymentProviders
      .find(p => p.paymentMode === PAYMENT_MODES.ADYEN_WEB_KLARNA_ACCOUNT);

    return paymentProvider?.installmentsNumber;
  }

  @computed
  get adyenPayPalPayLaterEligible(): boolean {
    const paymentProvider = this.paymentProviders
      .find(p => p.providerName === PAYMENT_PROVIDERS.ADYEN_WEB && p.paymentMode === PAYMENT_MODES.ADYEN_WEB_PAYPAL);

    return paymentProvider?.payLaterEligible;
  }

  @computed
  get adyenKlarnaPaymentEligible(): boolean {
    return this.paymentProviders
      .some(p => p.paymentMode === PAYMENT_MODES.ADYEN_WEB_KLARNA || p.paymentMode === PAYMENT_MODES.ADYEN_WEB_KLARNA_ACCOUNT);
  }
}
export default Payment;
